import React, { useState, useEffect } from 'react';
import Modal from '../ui/Modal';
import { RiCalendarLine, RiMoneyDollarCircleLine, RiAlertLine, RiFileTextLine, RiRefreshLine } from 'react-icons/ri';
import rentalExtensionService, { CanExtendResponse } from '../../services/rentalExtensionService';
import { useCreateRentalExtension } from '../../hooks/mutations/useRentalExtensionMutations';
import { showAlert } from '../../utils/alert';
import { cn } from '../../utils/cn';

interface RentalExtensionModalProps {
  isOpen: boolean;
  onClose: () => void;
  rentalId: number;
  rentalCode: string;
  currentEndDate: string;
  currentRentalFee?: number;
  onSuccess?: () => void;
}

const RentalExtensionModal: React.FC<RentalExtensionModalProps> = ({
  isOpen,
  onClose,
  rentalId,
  rentalCode,
  currentEndDate,
  currentRentalFee,
  onSuccess
}) => {
  const [loading, setLoading] = useState(false);
  const [canExtend, setCanExtend] = useState<CanExtendResponse | null>(null);
  const [extensionDays, setExtensionDays] = useState(30); // OBS-S244 FIX: default 30 วัน (safer — กันกดยืนยันเผลอต่อ 1 ปี)
  // BUG-PG-EXTFEE-ZEROTOTAL: '' = ไม่ระบุ (คงค่าเช่าเดิม) · 0 = ตั้งใจให้ฟรี — สองกรณีนี้
  // ต้องแยกกัน เพราะ backend เขียน rental.total_amount ตามค่าที่ส่งไปเมื่อไม่ใช่ null
  const [newRentalFee, setNewRentalFee] = useState<number | ''>('');
  const [notes, setNotes] = useState('');
  const [autoApprove, setAutoApprove] = useState(false);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  const createExtensionMutation = useCreateRentalExtension();

  // Check if can extend และ set ค่า default
  useEffect(() => {
    if (isOpen && rentalId) {
      checkCanExtend();
      // Set ค่าเช่าปัจจุบันเป็น default — เฉพาะเมื่อรู้ค่าจริงเท่านั้น
      // BUG-PG-EXTFEE-ZEROTOTAL: rental ที่ไม่มี rental_items จะได้ monthly_fee = 0 ซึ่งแปลว่า
      // "ไม่มีรายการให้คำนวณ" ไม่ใช่ "ค่าเช่า 0 บาท" — ถ้า prefill 0 ไว้แล้วผู้ใช้กดยืนยัน
      // ระบบจะเขียนทับ total_amount ของ rental เป็น 0.00 ทั้งที่ไม่มีใครตั้งใจแก้ค่าเช่า
      setNewRentalFee(currentRentalFee ? currentRentalFee : '');
    }
  }, [isOpen, rentalId, currentRentalFee]);

  const checkCanExtend = async () => {
    try {
      setLoading(true);
      const response = await rentalExtensionService.canExtend(rentalId);
      setCanExtend(response);
    } catch (error) {
      console.error('Error checking can extend:', error);
      showAlert('เกิดข้อผิดพลาด', 'ไม่สามารถตรวจสอบสถานะการต่อเช่าได้', 'error');
    } finally {
      setLoading(false);
    }
  };

  const calculateNewEndDate = () => {
    const date = new Date(currentEndDate);
    date.setDate(date.getDate() + extensionDays);
    return date.toLocaleDateString('th-TH', {
      year: '2-digit',
      month: 'long',
      day: 'numeric'
    });
  };

  const validateForm = () => {
    const errors: Record<string, string> = {};

    // Validate extension days
    if (!extensionDays || extensionDays <= 0) {
      errors.extensionDays = 'กรุณาระบุจำนวนวันที่ต้องการต่อ';
    }

    // Validate new rental fee — optional: เว้นว่าง = คงค่าเช่าเดิมไว้
    if (newRentalFee !== '' && newRentalFee < 0) {
      errors.newRentalFee = 'ค่าเช่าต้องมากกว่าหรือเท่ากับ 0';
    }

    // Validate notes length (optional field but check if too long)
    if (notes.length > 1000) {
      errors.notes = 'หมายเหตุต้องมีความยาวไม่เกิน 1,000 ตัวอักษร';
    }

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      const firstErrorField = Object.keys(errors)[0];
      const errorElement = document.querySelector(`[name="${firstErrorField}"]`);
      if (errorElement) {
        errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        (errorElement as HTMLInputElement).focus();
      }
      return false;
    }

    setFormErrors({});
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    createExtensionMutation.mutate(
      {
        rentalId,
        data: {
          extension_days: extensionDays,
          // BUG-PG-EXTFEE-ZEROTOTAL: เว้นว่าง → ไม่ส่งฟิลด์เลย เพื่อให้ backend คงค่าเช่าเดิม
          // (ส่ง 0 ไปจะถูกตีความว่า "ตั้งใจให้ฟรี" แล้วเขียนทับ total_amount เป็น 0)
          ...(newRentalFee === '' ? {} : { new_rental_fee: newRentalFee }),
          reason: 'ต่อการเช่า',  // ใช้ค่า default เสมอ
          notes: notes.trim()
        },
        autoApprove: true  // อนุมัติอัตโนมัติเสมอ
      },
      {
        onSuccess: () => {
          onClose();
          if (onSuccess) {
            onSuccess();
          }
        }
      }
    );
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`ต่อการเช่า - ${rentalCode}`}
      size="xl"
    >
      {loading ? (
        <div className="flex justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
        </div>
      ) : canExtend && !canExtend.can_extend ? (
        <div className="p-6">
          <div className="bg-danger-50 border border-danger-200 rounded-md p-4">
            <div className="flex">
              <RiAlertLine className="h-5 w-5 text-danger-400" />
              <div className="ml-3">
                <h3 className="text-sm font-medium text-danger-800">
                  ไม่สามารถต่อการเช่าได้
                </h3>
                <p className="mt-2 text-sm text-danger-700">{canExtend.message}</p>
              </div>
            </div>
          </div>
          <div className="mt-4 flex justify-end">
            <button
              type="button"
              onClick={onClose}
              className="btn btn-secondary"
            >
              ปิด
            </button>
          </div>
        </div>
      ) : (
        <form onSubmit={handleSubmit} noValidate className="space-y-6 p-6">
          {/* Current Info */}
          <div className="bg-secondary-50 rounded-lg p-4">
            <div className="flex items-center gap-2 pb-2 border-b border-secondary-200 mb-3">
              <div className="w-8 h-8 bg-gradient-to-br from-primary-500 to-primary-600 rounded-lg flex items-center justify-center shadow-sm">
                <RiFileTextLine className="w-4 h-4 text-white" />
              </div>
              <h3 className="text-base font-semibold text-secondary-800">ข้อมูลปัจจุบัน</h3>
            </div>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-secondary-500">วันสิ้นสุดปัจจุบัน:</span>
                <span className="ml-2 font-medium">
                  {new Date(currentEndDate).toLocaleDateString('th-TH', {
                    year: '2-digit',
                    month: 'long',
                    day: 'numeric'
                  })}
                </span>
              </div>
              <div>
                <span className="text-secondary-500">ค่าเช่าปัจจุบัน:</span>
                <span className="ml-2 font-medium">
                  {currentRentalFee ? `${currentRentalFee.toLocaleString()} บาท/เดือน` : '-'}
                </span>
              </div>
              {canExtend && (
                <>
                  <div>
                    <span className="text-secondary-500">จำนวนวันที่เหลือ:</span>
                    <span className="ml-2 font-medium">{canExtend.days_left} วัน</span>
                  </div>
                  <div>
                    <span className="text-secondary-500">ต่อมาแล้ว:</span>
                    <span className="ml-2 font-medium">{canExtend.extension_count} ครั้ง</span>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Extension Form */}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-secondary-700 mb-1">
                <RiCalendarLine className="inline w-4 h-4 mr-1" />
                ระยะเวลาที่ต้องการต่อ <span className="text-danger-600">*</span>
              </label>
              <select
                name="extensionDays"
                value={extensionDays}
                onChange={(e) => {
                  setExtensionDays(Number(e.target.value));
                  if (formErrors.extensionDays) {
                    setFormErrors(prev => {
                      const updated = { ...prev };
                      delete updated.extensionDays;
                      return updated;
                    });
                  }
                }}
                className={cn('select', formErrors.extensionDays && 'border-danger-300')}
                aria-invalid={Boolean(formErrors.extensionDays)}
                aria-describedby={formErrors.extensionDays ? 'extensionDays-error' : undefined}
              >
                <option value={30}>30 วัน (1 เดือน)</option>
                <option value={60}>60 วัน (2 เดือน)</option>
                <option value={90}>90 วัน (3 เดือน)</option>
                <option value={180}>180 วัน (6 เดือน)</option>
                <option value={365}>365 วัน (1 ปี)</option>
              </select>
              {formErrors.extensionDays && (
                <p id="extensionDays-error" className="mt-1 text-sm text-danger-600">{formErrors.extensionDays}</p>
              )}
              <p className="mt-1 text-sm text-secondary-500">
                วันสิ้นสุดใหม่: {calculateNewEndDate()}
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-secondary-700 mb-1">
                <RiMoneyDollarCircleLine className="inline w-4 h-4 mr-1" />
                ค่าเช่าใหม่ (บาท/เดือน)
              </label>
              <input
                type="number"
                name="newRentalFee"
                value={newRentalFee}
                placeholder="เว้นว่างไว้ = ใช้ค่าเช่าเดิม"
                onChange={(e) => {
                  const raw = e.target.value;
                  setNewRentalFee(raw === '' ? '' : Number(raw));
                  if (formErrors.newRentalFee) {
                    setFormErrors(prev => {
                      const updated = { ...prev };
                      delete updated.newRentalFee;
                      return updated;
                    });
                  }
                }}
                className={cn('input', formErrors.newRentalFee && 'border-danger-300')}
                min="0"
                step="0.01"
                aria-invalid={Boolean(formErrors.newRentalFee)}
                aria-describedby={formErrors.newRentalFee ? 'newRentalFee-error' : undefined}
              />
              {formErrors.newRentalFee && (
                <p id="newRentalFee-error" className="mt-1 text-sm text-danger-600">{formErrors.newRentalFee}</p>
              )}
              {!formErrors.newRentalFee && newRentalFee === '' && (
                <p className="mt-1 text-sm text-secondary-500">
                  ไม่ระบุ = คงค่าเช่าเดิมของสัญญาไว้ (กรอก 0 เฉพาะกรณีต้องการให้ฟรีจริง)
                </p>
              )}
              {!formErrors.newRentalFee && newRentalFee !== '' && newRentalFee !== currentRentalFee && (
                <p className="mt-1 text-sm text-warning-600">
                  {newRentalFee > (currentRentalFee || 0)
                    ? `เพิ่มขึ้น ${((newRentalFee - (currentRentalFee || 0)) / (currentRentalFee || 1) * 100).toFixed(1)}%`
                    : `ลดลง ${(((currentRentalFee || 0) - newRentalFee) / (currentRentalFee || 1) * 100).toFixed(1)}%`
                  }
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-secondary-700 mb-1">
                หมายเหตุเพิ่มเติม
              </label>
              <textarea
                aria-label="หมายเหตุเพิ่มเติม"
                name="notes"
                value={notes}
                onChange={(e) => {
                  setNotes(e.target.value);
                  if (formErrors.notes) {
                    setFormErrors(prev => {
                      const updated = { ...prev };
                      delete updated.notes;
                      return updated;
                    });
                  }
                }}
                className={cn(
                  'input',
                  formErrors.notes && 'border-danger-300'
                )}
                rows={3}
                placeholder="หมายเหตุเพิ่มเติม (ถ้ามี)"
                maxLength={1000}
                aria-invalid={Boolean(formErrors.notes)}
                aria-describedby={formErrors.notes ? 'notes-error' : undefined}
              />
              {formErrors.notes && (
                <p id="notes-error" className="mt-1 text-sm text-danger-600">{formErrors.notes}</p>
              )}
              <p className="mt-1 text-sm text-secondary-500">
                {notes.length}/1,000 ตัวอักษร
              </p>
            </div>

            {/* ซ่อน Auto Approve Option เพราะอนุมัติอัตโนมัติเสมอ */}
            <div className="flex items-center hidden">
              <input
                type="checkbox"
                id="autoApprove"
                checked={true}
                onChange={(e) => {}}
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-secondary-300 rounded"
              />
              <label htmlFor="autoApprove" className="ml-2 block text-sm text-secondary-700">
                อนุมัติอัตโนมัติ
              </label>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end space-x-3 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="btn btn-secondary"
              disabled={createExtensionMutation.isPending}
            >
              ยกเลิก
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={createExtensionMutation.isPending}
            >
              {createExtensionMutation.isPending ? (
                <span className="flex items-center">
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  กำลังบันทึก...
                </span>
              ) : (
                'ต่อการเช่า'
              )}
            </button>
          </div>
        </form>
      )}
    </Modal>
  );
};

export default RentalExtensionModal;